import { ThemeEvents, VariantSelectedEvent } from '@theme/events';

class LpsAgePicker extends HTMLElement {
  connectedCallback() {
    if (this.controller || this.closest('quick-add-dialog, product-card')) return;
    this.product = JSON.parse(this.querySelector('[data-picker-product]').textContent);
    this.names = this.product.options.map(o => typeof o === 'string' ? o : o.name);
    this.sizeIndex = this.names.findIndex(n => /^size$/i.test(n));
    this.colorIndex = this.names.findIndex((n, i) => i !== this.sizeIndex);
    if (this.sizeIndex < 0 || this.names.length > 2) return;
    this.hasColor = this.colorIndex >= 0;
    this.controller = new AbortController();
    this.labels = JSON.parse(this.querySelector('[data-picker-labels]').textContent);
    this.labels.gray_hint = this.querySelector('[data-picker-gray-hint]').textContent;
    this.fallbackMaker = this.querySelector('[data-picker-fallback-maker]').textContent;
    this.asPictured = this.querySelector('[data-picker-as-pictured]').textContent;
    this.styleLabel = this.querySelector('[data-picker-style-label]').textContent;
    this.section = this.closest('.shopify-section');
    this.isFarmersRollout = Boolean(this.section.querySelector('[data-farmers-layout]'));
    this.isPrinceton = /^Princeton Panthers/i.test(this.product.title) || ["9697354285337","10270467817753","10270469751065","10311863435545","15414254108953","15442700927257","15451833172249","15451848605977"].includes(String(this.product.id));
    this.classList.toggle('lps-is-princeton',this.isPrinceton);
    this.exactPriceOnly = this.isFarmersRollout || ["15463911162137","15463912669465","15463913226521","15463913554201","15463913816345","15463914111257","15463914373401","15463914602777","15463914832153","15463915127065","15463916273945","15463917224217"].includes(String(this.product.id)) || this.product.title.startsWith('Farmersville Nothing But Net Basketball Sport-Tek') || /^Farmersville (?:Blessed to Ball Basketball|Game Day Basketball Collage)/.test(this.product.title) || this.isPrinceton || ["15457629896985","15457662370073","15457662664985","15457688224025","15457688617241","15457689075993","15462997754137","15463122403609","15463533707545"].includes(String(this.product.id));
    this.variants = this.product.variants;
    this.ages = ['Adult','Youth','Toddler'].filter(a => this.variants.some(v => this.ageOf(v) === a));
    this.age = this.ages.length === 1 ? this.ages[0] : '';
    const initialColors = [...new Set(this.variants.filter(v => this.ageOf(v) === this.age && v.available).map(v => this.colorOf(v)))];
    this.color = this.hasColor && initialColors.length === 1 ? initialColors[0] : '';
    this.size = '';
    this.busy = false;
    this.expectedId = '';
    this.confirmedId = '';
    const linkedId = new URL(location.href).searchParams.get('variant');
    const linked = this.variants.find(v => String(v.id) === linkedId && v.available);
    if (linked) {
      this.age = this.ageOf(linked);
      this.color = this.colorOf(linked);
      this.size = this.sizeOf(linked);
      this.confirmedId = String(linked.id);
    }
    this.addEventListener('click', event => this.choose(event), { signal:this.controller.signal });
    this.section.addEventListener(ThemeEvents.variantUpdate, event => {
      if (String(event.detail.data.productId) !== String(this.product.id)) return;
      this.confirmedId = String(event.detail.resource?.id || '');
      if (this.confirmedId === this.expectedId) this.busy = false;
      if (!this.selected()) {
        const url = new URL(location.href);
        url.searchParams.delete('variant');
        history.replaceState({}, '', url);
      }
      queueMicrotask(() => this.render());
    }, { signal:this.controller.signal });
    this.section.addEventListener('submit', event => {
      const form = event.target.closest('product-form-component');
      if (String(form?.dataset.productId) === String(this.product.id) && !this.canPurchase()) {
        event.preventDefault(); event.stopImmediatePropagation(); this.refreshPurchase();
      }
    }, { capture:true, signal:this.controller.signal });
    this.hidden = false;
    this.dataset.ready = 'true';
    this.render();
  }
  disconnectedCallback() { this.controller?.abort(); clearTimeout(this.timer); this.controller = null; }
  option(v, index) { return index < 0 ? '' : String(v.options?.[index] ?? v['option' + (index + 1)] ?? ''); }
  sizeOf(v) { return this.option(v, this.sizeIndex); }
  colorOf(v) { return this.option(v, this.colorIndex); }
  ageOf(v) {
    const size = this.sizeOf(v);
    if (/youth|\bY(?:XS|S|M|L|XL)\b/i.test(size)) return 'Youth';
    if (/^\d+(?:\/\d+)?T$|toddler/i.test(size)) return 'Toddler';
    return 'Adult';
  }
  matching() { return this.variants.filter(v => this.ageOf(v) === this.age); }
  selected() { return this.matching().find(v => this.sizeOf(v) === this.size && (!this.hasColor || this.colorOf(v) === this.color) && v.available); }
  canPurchase() { const v = this.selected(); return Boolean(v && !this.busy && String(v.id) === this.confirmedId); }
  field(name) { return this.querySelector('[data-picker-' + name + ']'); }
  nativePicker() { return this.section.querySelector('variant-picker[data-product-id="' + this.product.id + '"]'); }
  maker(value) {
    if (/^Sport-Tek ST350(?:LS)?\b/.test(value)) return value.match(/^Sport-Tek ST350(?:LS)?/)[0];
    if (value.includes(' – ')) return value.split(' – ')[0];
    if (String(this.product.id) === '9088901218585') return this.age === 'Toddler' ? 'Rabbit Skins' : 'Augusta Sportswear';
    const native = this.nativePicker();
    if (native?.dataset.tt41Sop === 'true') return 'Team 365';
    if (this.age === 'Toddler' && /Rabbit Skins/i.test(this.product.description || '')) return 'Rabbit Skins';
    const input = [...(native?.querySelectorAll('input') || [])].find(i => i.value === value && i.name.startsWith(this.names[this.colorIndex] + '-'));
    const maker = input?.closest('label')?.querySelector('.sop12-button-maker')?.textContent.trim();
    if (this.age === 'Toddler' && /^Gildan/i.test(maker || '')) return this.fallbackMaker;
    return maker || this.fallbackMaker;
  }
  button(kind, value, selected, disabled) {
    const b = document.createElement('button');
    b.type = 'button'; b.dataset.choice = kind; b.dataset.value = value;
    b.setAttribute('aria-pressed', String(selected)); b.disabled = disabled;
    if (kind === 'color') {
      const m = document.createElement('span'); m.className = 'lps-picker-maker'; m.textContent = this.maker(value);
      const c = document.createElement('span'); c.className = 'lps-picker-color'; c.textContent = /^Sport-Tek ST350(?:LS)?\b/.test(value) ? value.replace(/^Sport-Tek ST350(?:LS)?\s*/, '') : value.includes(' – ') ? value.split(' – ').slice(1).join(' – ') : value;
      b.append(m, c);
    } else b.textContent = kind === 'age' ? this.labels[value] : value.replace(/^Youth\s+/i, '');
    if (disabled) b.setAttribute('aria-label', value + ' — ' + this.labels.gray_hint);
    return b;
  }
  render() {
    const native = this.nativePicker();
    if (native) {
      native.hidden = true;
      native.inert = true;
      native.querySelectorAll('input,select,button').forEach(control => { control.disabled = true; });
    }
    const focused = this.contains(document.activeElement) ? document.activeElement.closest('button')?.dataset : null;
    this.field('ages').replaceChildren(...this.ages.map(a => this.button('age', a, this.age === a, false)));
    this.field('options').hidden = !this.age;
    this.field('order-hint').hidden = !this.hasColor;
    const rows = this.matching();
    const colors = [...new Set(rows.map(v => this.colorOf(v)))];
    if (this.hasColor) {
      this.field('colors').replaceChildren(...colors.map(c => this.button('color', c, this.color === c, !rows.some(v => v.available && this.colorOf(v) === c && (this.isFarmersRollout || !this.size || this.sizeOf(v) === this.size)))));
    } else {
      const style = document.createElement('div'); style.className = 'lps-picker-fixedstyle';
      const maker = document.createElement('span'); maker.className = 'lps-picker-maker'; maker.textContent = String(this.product.id) === '9674307502361' ? (this.age === 'Toddler' ? 'Rabbit Skins' : 'Gildan Softstyle') : this.product.title.match(/Comfort Colors|Gildan|Rabbit Skins|Team 365|Augusta Sportswear|A4/i)?.[0] || this.styleLabel;
      const color = document.createElement('span'); color.className = 'lps-picker-color'; color.textContent = String(this.product.id) === '9674307502361' ? 'Maroon' : this.asPictured;
      style.append(maker, color); this.field('colors').replaceChildren(style);
    }
    const rank = value => {
      const s = value.replace(/^(Youth|Adult)\s+/i, '').toUpperCase().replace(/[-\s]/g, '');
      const order = ['XXS','XS','XSMALL','S','SMALL','M','MEDIUM','L','LARGE','XL','XLARGE','2X','2XL','3X','3XL','4X','4XL','5X','5XL','6X','6XL'];
      return /T$/.test(s) ? Number.parseInt(s) : order.includes(s) ? order.indexOf(s) : 100;
    };
    const sizes = [...new Set(rows.map(v => this.sizeOf(v)))].sort((a,b) => rank(a) - rank(b));
    this.field('sizes').replaceChildren(...sizes.map(s => this.button('size', s, this.size === s, !rows.some(v => v.available && this.sizeOf(v) === s && (!this.hasColor || !this.color || this.colorOf(v) === this.color)))));
    this.field('clear').hidden = !this.size;
    const priced = (this.age ? rows : this.variants).filter(v => (!this.hasColor || !this.color || this.colorOf(v) === this.color) && (!this.size || this.sizeOf(v) === this.size));
    const prices = priced.map(v => v.price);
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency', currency:this.product.currency || 'USD'}).format(cents / 100);
    const lo = Math.min(...prices), hi = Math.max(...prices);
    const exact = this.selected();
    this.field('price').textContent = this.exactPriceOnly
      ? exact ? money(exact.price) : (this.labels.price_prompt || 'Choose options to see your price')
      : prices.length ? lo === hi ? money(lo) : money(lo) + '–' + money(hi) : '';
    this.field('price').classList.toggle('lps-picker-price--prompt', this.exactPriceOnly && !exact);
    this.field('price').setAttribute('aria-live', 'polite');
    if (this.isPrinceton && (!this.hasColor || this.color)) {
      this.field('sizes').querySelectorAll('button[data-choice="size"]').forEach(button => {
        const match = rows.find(v => v.available && this.sizeOf(v) === button.dataset.value && (!this.hasColor || this.colorOf(v) === this.color));
        if (!match) return;
        const amount = document.createElement('span'); amount.className = 'lps-picker-size-price'; amount.textContent = money(match.price);
        button.append(amount);
      });
    }
    this.dispatchEvent(new CustomEvent('lps:selection-change', {bubbles:true}));
    this.refreshPurchase();
    if (focused?.choice) [...this.querySelectorAll('button[data-choice]')].find(b => b.dataset.choice === focused.choice && b.dataset.value === focused.value)?.focus({preventScroll:true});
  }
  choose(event) {
    const b = event.target.closest('button');
    if (!b || b.disabled) return;
    if (b.hasAttribute('data-picker-clear')) { this.size = ''; this.render(); return; }
    const {choice, value} = b.dataset;
    if (!choice) return;
    if (choice === 'age') {
      const previous=this.color,brand=v=>this.maker(v).replace(/\b(?:64000B?|5000B?|1717|9018|18000B?|18500B?|1566)\b|Youth|Adult/gi,'').trim(),shade=v=>(String(v).includes(' – ')?String(v).split(' – ').slice(1).join(' – '):String(v)).toLowerCase().replace(/grey|heather/g,'gray').replace(/crimson/g,'red');
      this.age=value;const rows=this.matching().filter(v=>v.available),candidate=rows.find(v=>brand(this.colorOf(v))===brand(previous)&&shade(this.colorOf(v))===shade(previous))||rows.find(v=>this.colorOf(v)===previous);
      this.color=candidate?this.colorOf(candidate):'';this.size='';this.confirmedId='';this.expectedId='';this.busy=false;
    }
    if (choice === 'color') {this.color = value;if(this.isFarmersRollout&&!this.matching().some(v=>v.available&&this.colorOf(v)===value&&this.sizeOf(v)===this.size))this.size='';}
    if (choice === 'size') this.size = value;
    this.render();
    if (choice === 'age' && !this.color) return;
    const variant = this.selected() || this.matching().find(v => v.available && (!this.hasColor || !this.color || this.colorOf(v) === this.color) && (!this.size || this.sizeOf(v) === this.size));
    if (variant) this.sync(variant);
  }
  async sync(variant) {
    this.busy = true; this.expectedId = String(variant.id); this.refreshPurchase();
    await customElements.whenDefined('variant-picker');
    if (this.expectedId !== String(variant.id) || !this.isConnected) return;
    const picker = this.nativePicker();
    const inputs = [...picker.querySelectorAll('input')];
    let trigger;
    for (const [index, name] of this.names.entries()) {
      const input = inputs.find(i => i.name.startsWith(name + '-') && i.value === this.option(variant, index));
      if (!input) {
        // Native themes omit selectors for options with one fixed value.
        // The explicit variant URL below still identifies that complete variant.
        const values = [...new Set(this.variants.map(v => this.option(v, index)))];
        if (values.length === 1 && values[0] === this.option(variant, index)) continue;
        this.busy = false; this.confirmedId = ''; this.refreshPurchase(this.labels.retry); return;
      }
      input.disabled = false; picker.updateSelectedOption(input); trigger = input;
    }
    if (!trigger) { this.busy = false; this.confirmedId = ''; this.refreshPurchase(this.labels.retry); return; }
    picker.dispatchEvent(new VariantSelectedEvent({id:trigger.dataset.optionValueId || ''}));
    const url = new URL(picker.dataset.productUrl, location.origin);
    url.searchParams.set('variant', String(variant.id));
    url.searchParams.set('section_id', picker.dataset.sectionId);
    picker.fetchUpdatedSection(url.toString());
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { if (this.busy) this.refreshPurchase(this.labels.retry); }, 12000);
  }
  refreshPurchase(message) {
    const valid = this.canPurchase();
    this.section.querySelectorAll('product-form-component').forEach(form => {
      if (String(form.dataset.productId) !== String(this.product.id)) return;
      form.querySelectorAll('[type="submit"]').forEach(b => { b.disabled = !valid; });
      form.querySelectorAll('.shopify-payment-button, [ref="acceleratedCheckoutButtonContainer"]').forEach(e => { e.hidden = !valid; });
    });
    this.field('status').textContent = message || (!this.age ? this.labels.begin : this.busy ? this.labels.loading : this.hasColor && !this.color && !this.size ? this.labels.choose : this.hasColor && !this.color ? this.labels.choose_color : !this.size ? this.labels.choose_size : valid ? this.labels.ready : this.labels.loading);
    this.dataset.purchaseReady = String(valid);
  }
}
if (!customElements.get('lps-age-picker')) customElements.define('lps-age-picker', LpsAgePicker);






